import { useEffect, useRef, useState } from 'react'

import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors'
import { useApolloClient } from '@apollo/client/react'

import { useCallbackRef } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { PusherEvent, usePusherEvent } from '~publish/services/pusher'

import { useCommentsCountsTracking } from '../tracking/useCommentsCountsTracking'
import { markPostGainedBackfilledComments } from './backfilledPostCommentsSignal'

export const CommentsCountsByPostQuery = graphql(`
  query CommentsCountsByPostQuery($input: CommentsCountsByPostInput!) {
    commentsCountsByPost(input: $input) {
      counts {
        postId
        comments {
          unreplied
          replied
          dismissed
        }
      }
    }
  }
`)

export type CommentCountsByPost = {
  unreplied: number
  replied: number
  dismissed: number
}

type UseCommentsCountsByPostResult = {
  countsByPostId: Record<string, CommentCountsByPost>
  loading: boolean
  error: Error | undefined
}

type UseCommentsCountsByPostOptions = {
  /**
   * True when every post in `postIds` is known to have at least one countable
   * comment, which holds only while the list itself is filtered to posts with
   * comments. It gates the omitted-row signal: the counts aggregation omits a
   * post with no matching comments, so an absent row is an invariant violation
   * under this precondition and expected without it. Defaults to false, so a
   * caller reports nothing until it asserts the precondition.
   */
  everyPostHasComments?: boolean
}

type CommentsCountsRow = NonNullable<
  NonNullable<
    ResultOf<typeof CommentsCountsByPostQuery>['commentsCountsByPost']
  >['counts']
>[number]

/** The backend rejects a request that carries more post IDs than this. */
const MAX_POST_IDS_PER_REQUEST = 40

function toCountsByPostId(
  rows: readonly CommentsCountsRow[],
): Record<string, CommentCountsByPost> {
  const countsByPostId: Record<string, CommentCountsByPost> = {}

  for (const row of rows) {
    if (row.postId != null && row.comments != null) {
      countsByPostId[row.postId] = {
        unreplied: row.comments.unreplied,
        replied: row.comments.replied,
        dismissed: row.comments.dismissed,
      }
    }
  }

  return countsByPostId
}

function toError(reason: unknown): Error {
  return reason instanceof Error
    ? reason
    : new Error('Failed to fetch comment counts by post')
}

/**
 * The HTTP status a transport-level failure carries, when it carries one. A
 * connection drop has none, which is why the tracking field allows null. The
 * property check covers a link that raises its own status-carrying error
 * instead of Apollo's `ServerError`.
 */
function toStatusCode(reason: unknown): number | null {
  if (ServerError.is(reason)) return reason.statusCode

  if (
    typeof reason === 'object' &&
    reason !== null &&
    'statusCode' in reason &&
    typeof reason.statusCode === 'number'
  ) {
    return reason.statusCode
  }

  return null
}

/**
 * Which side a counts failure came from. `CombinedGraphQLErrors` means the
 * server ran the operation and answered with GraphQL errors; anything else
 * failed in transport, before an answer existed. Classifying the error itself
 * rather than the branch it arrived on matters because `errorPolicy` decides
 * whether a request resolves with an error or rejects, and both kinds of
 * failure travel the same way under either setting.
 */
function toErrorSource(reason: unknown): 'frontend' | 'backend' {
  return CombinedGraphQLErrors.is(reason) ? 'backend' : 'frontend'
}

/**
 * Reports every post whose unreplied count grew between two count snapshots.
 * A post that had no counts in the first snapshot is skipped: with nothing to
 * compare against, growth cannot be told from a first reading.
 */
function reportPostsThatGainedComments(
  before: Record<string, CommentCountsByPost>,
  after: Record<string, CommentCountsByPost>,
): void {
  for (const [postId, counts] of Object.entries(after)) {
    const previous = before[postId]
    if (previous && counts.unreplied > previous.unreplied) {
      markPostGainedBackfilledComments(postId)
    }
  }
}

function chunkPostIds(postIds: string[]): string[][] {
  const chunks: string[][] = []

  for (let i = 0; i < postIds.length; i += MAX_POST_IDS_PER_REQUEST) {
    chunks.push(postIds.slice(i, i + MAX_POST_IDS_PER_REQUEST))
  }

  return chunks
}

/**
 * Fetches comment counts (unreplied, replied, dismissed) for the given post IDs.
 *
 * Results accumulate: each new post ID (e.g. from infinite-scroll pagination)
 * is queued once, requested in chunks that respect the backend limit of 40 IDs
 * per request, and merged into the existing map. The queue is what keeps a
 * long or fast-growing list fully counted — IDs past the first chunk wait for a
 * request instead of being dropped, and a burst that lands mid-request joins
 * the running drain rather than replacing it.
 *
 * @param postIds - List of post IDs to fetch counts for. No request is made when empty.
 * @param options - See {@link UseCommentsCountsByPostOptions}.
 */
export function useCommentsCountsByPost(
  postIds: string[],
  { everyPostHasComments = false }: UseCommentsCountsByPostOptions = {},
): UseCommentsCountsByPostResult {
  const [countsByPostId, setCountsByPostId] = useState<
    Record<string, CommentCountsByPost>
  >({})
  const [loading, setLoading] = useState(() => postIds.length > 0)
  const [error, setError] = useState<Error | undefined>(undefined)

  const client = useApolloClient()

  const { trackOmittedCountsRows, trackCountsRequestFailed } =
    useCommentsCountsTracking()

  // Every ID this hook has already asked the backend for. Keeps an ID from
  // being requested twice, and marks which posts the hook tracks.
  const requestedIds = useRef<Set<string>>(new Set())
  // IDs waiting for a request slot.
  const queuedIds = useRef<string[]>([])
  const isDraining = useRef(false)
  // Mirrors `countsByPostId` for reads that cannot wait for a re-render, so a
  // refetch can be compared against the counts it replaces.
  const latestCounts = useRef<Record<string, CommentCountsByPost>>({})

  /**
   * Requests counts for the given IDs in chunks and merges what comes back.
   * Returns the first failure so the caller decides whether to surface it.
   *
   * Every request the hook makes passes through here, including the
   * Pusher-driven refreshes, so each one is reported even when its caller
   * discards the returned failure.
   *
   * Each chunk gets its own `client.query`, because counts requests overlap:
   * a long list fires every chunk at once, and a Pusher refresh lands while a
   * drain still runs. Requests that share one observable query cancel each
   * other, which leaves the cancelled chunk's posts with no counts at all.
   */
  const fetchCountsForPosts = useCallbackRef(
    async (targetPostIds: string[]): Promise<Error | undefined> => {
      if (targetPostIds.length === 0) return undefined

      const chunks = chunkPostIds(targetPostIds)
      const results = await Promise.allSettled(
        chunks.map((chunk) =>
          client.query({
            query: CommentsCountsByPostQuery,
            variables: { input: { postIds: chunk } },
            fetchPolicy: 'network-only',
            // `errorPolicy: 'all'` keeps the rows a partly-failed response did
            // return, which the default policy discards along with the whole
            // result. It also delivers the failure on the result instead of
            // rejecting, so a chunk that errors still contributes the counts
            // it has.
            errorPolicy: 'all',
          }),
        ),
      )

      const incoming: Record<string, CommentCountsByPost> = {}
      let failure: Error | undefined

      for (const [index, result] of results.entries()) {
        const requestedPostIds = chunks[index]

        if (result.status === 'rejected') {
          const rejection = toError(result.reason)
          failure ??= rejection
          trackCountsRequestFailed({
            requestedPostIds,
            errorMessage: rejection.message,
            statusCode: toStatusCode(result.reason),
            source: toErrorSource(result.reason),
          })
          continue
        }

        failure ??= result.value.error

        const rows = result.value.data?.commentsCountsByPost?.counts
        const countsForChunk = toCountsByPostId(rows ?? [])
        Object.assign(incoming, countsForChunk)

        if (result.value.error) {
          trackCountsRequestFailed({
            requestedPostIds,
            errorMessage: result.value.error.message,
            statusCode: toStatusCode(result.value.error),
            source: toErrorSource(result.value.error),
          })
          continue
        }

        // The aggregation returns no row for a post with no matching comments,
        // so an absent row is only a problem while the list guarantees every
        // post has one.
        if (everyPostHasComments) {
          trackOmittedCountsRows({
            requestedPostIds,
            missingPostIds: requestedPostIds.filter(
              (postId) => !(postId in countsForChunk),
            ),
          })
        }
      }

      if (Object.keys(incoming).length > 0) {
        latestCounts.current = { ...latestCounts.current, ...incoming }
        setCountsByPostId((prev) => ({ ...prev, ...incoming }))
      }

      return failure
    },
  )

  // Empties the queue, one batch at a time. Only one batch is in flight, so IDs
  // queued during a request wait for the pass that follows instead of
  // replacing it.
  //
  // `error` describes the pass, not the last batch in it: a pass starts clean
  // and keeps its first failure. Letting a later success overwrite it would
  // report success while the failed batch's posts still have no counts.
  const drainQueuedIds = useCallbackRef(async (): Promise<void> => {
    if (isDraining.current) return
    isDraining.current = true
    setError(undefined)

    try {
      while (queuedIds.current.length > 0) {
        const batch = queuedIds.current
        queuedIds.current = []
        // eslint-disable-next-line no-await-in-loop -- the next batch is only known once this one settles
        const failure = await fetchCountsForPosts(batch)
        if (failure) {
          setError((previous) => previous ?? failure)
        }
      }
    } finally {
      isDraining.current = false
      setLoading(false)
    }
  })

  useEffect(() => {
    const newIds: string[] = []

    for (const id of postIds) {
      if (requestedIds.current.has(id)) continue
      requestedIds.current.add(id)
      newIds.push(id)
    }

    if (newIds.length === 0) return

    queuedIds.current.push(...newIds)
    setLoading(true)

    void drainQueuedIds()
  }, [postIds, drainQueuedIds])

  // ── Real-time updates via Pusher ──────────────────────────────────

  // A post this hook has requested counts for, or already holds counts for.
  // An event for any other post is ignored: the posts list decides which posts
  // get counts here.
  const isTrackedPost = (postId: string): boolean =>
    requestedIds.current.has(postId) || postId in countsByPostId

  const refetchAllListedPosts = (): void => {
    const listedIds = Array.from(new Set(postIds))
    if (listedIds.length > 0) {
      void fetchCountsForPosts(listedIds)
    }
  }

  // Single-comment events: reply, dismiss, undismiss
  usePusherEvent(
    [
      PusherEvent.COMMENT_REPLIED,
      PusherEvent.COMMENT_DISMISSED,
      PusherEvent.COMMENT_UNDISMISSED,
    ],
    (eventData: { comment?: { postId?: string } }) => {
      const postId = eventData?.comment?.postId
      if (postId && isTrackedPost(postId)) {
        void fetchCountsForPosts([postId])
      }
    },
  )

  // New comment ingested on a channel: refresh the affected post's counts.
  // A tracked post with no counts is included, so a post the initial request
  // missed stops showing a blank badge as soon as a comment arrives for it.
  usePusherEvent(
    PusherEvent.COMMENT_INGESTED,
    (eventData: { comment?: { postId?: string } }) => {
      const postId = eventData?.comment?.postId
      if (postId && isTrackedPost(postId)) {
        void fetchCountsForPosts([postId])
      }
    },
  )

  // Channel backfill completed with new comments: refetch every listed post
  // since per-comment COMMENT_INGESTED events may not fire for bulk backfills.
  // The same refetch says which posts actually gained comments — the event
  // itself names no post — so every post whose unreplied count grew is
  // reported for the "New comments available" pill.
  usePusherEvent(
    PusherEvent.CHANNEL_BACKFILL_DONE,
    async (eventData: { newCommentsCount?: number }) => {
      if (!eventData?.newCommentsCount || eventData.newCommentsCount <= 0) {
        return
      }

      const listedIds = Array.from(new Set(postIds))
      if (listedIds.length === 0) return

      const countsBeforeBackfill = latestCounts.current
      await fetchCountsForPosts(listedIds)
      reportPostsThatGainedComments(countsBeforeBackfill, latestCounts.current)
    },
  )

  // Dismiss all comments for a specific post
  usePusherEvent(
    PusherEvent.DISMISS_ALL_COMMENTS_BY_POST,
    (eventData: { postId?: string }) => {
      const postId = eventData?.postId
      if (postId && isTrackedPost(postId)) {
        void fetchCountsForPosts([postId])
      }
    },
  )

  // Bulk dismiss events: refetch every listed post
  usePusherEvent(
    [
      PusherEvent.DISMISS_ALL_COMMENTS_BY_CHANNEL,
      PusherEvent.DISMISS_ALL_COMMENTS_BY_ORGANIZATION,
      PusherEvent.DISMISS_ALL_COMMENTS_FOR_CHANNELS,
    ],
    () => {
      refetchAllListedPosts()
    },
  )

  return { countsByPostId, loading, error }
}
